import cn from 'classnames';
import type { ComponentProps } from 'react';
import { useMemo } from 'react';
import { CountryFlag } from 'shared/src/CountryFlag';

import { formatDecimalAmount } from 'utils/formatters/formatCurrency';
import { getCountryName } from 'utils/formatters/getCountryName';

import { BusinessUnitDisplay } from '@pages/Payouts/NewCreateManualPayout/BusinessUnitDisplay';
import { InlineVirtualAccount } from '@pages/Payouts/NewCreateManualPayout/InlineVirtualAccount';

import { FormattedDate } from '@atoms/FormattedDate/FormattedDate';

import type { Previewable } from './utils/fieldPreviewEnhancer';

type ManualPayoutPreviewFieldProps = {
  field: Previewable;
  displayEnd?: boolean;
  className?: ComponentProps<'div'>['className'];
};

export const ManualPayoutPreviewField = (
  props: ManualPayoutPreviewFieldProps
) => {
  const children = useMemo(() => {
    return fieldElementFactory(props);
  }, [props]);

  return (
    <div
      className={cn(
        'flex w-full flex-col gap-1',
        props.displayEnd && 'text-end'
      )}
    >
      <span className="text-sm opacity-50">{props.field.label}</span>
      {children}
    </div>
  );
};

const fieldElementFactory = ({
  field,
  className,
  displayEnd
}: ManualPayoutPreviewFieldProps) => {
  switch (field.kind) {
    case 'text':
      return <span className={className}>{field.value}</span>;

    case 'amount':
      return (
        <span className={className}>
          {formatDecimalAmount(Number(field.value))}
        </span>
      );

    case 'virtual_account':
      return <InlineVirtualAccount accountNumber={field.value} />;

    case 'iban':
      return <span className={className}>{field.value}</span>;

    case 'country':
      return (
        <div
          className={cn(
            'flex flex-row items-center gap-2',
            displayEnd && 'justify-end'
          )}
        >
          <CountryFlag className="!size-[18px]" countryCode={field.value} svg />
          {getCountryName(field.value)} ({field.value})
        </div>
      );

    case 'business_unit':
      return (
        <BusinessUnitDisplay businessUnit={field.value} className={className} />
      );

    case 'date':
      return <FormattedDate value={field.value} className={className} />;

    default:
      return <span className={className}>{field.value}</span>;
  }
};
